import type React from 'react';

import { AccountTree, Check, Code, ContentCopy, Edit, OpenInFull, UnfoldLess, UnfoldMore, WrapText } from '@mui/icons-material';
import { IconButton, Stack, Tooltip } from '@mui/material';

import type { JsonDisplayMode } from './JsonDisplayTypes';

interface JsonDisplayActionsProps {
	asAccordion?: boolean;
	copied: boolean;
	displayMode: JsonDisplayMode;
	editable: boolean;
	editing?: boolean;
	showOpenInDialog?: boolean;
	wrapText: boolean;
	onCollapseAll: () => void;
	onCopy: () => void | Promise<void>;
	onEdit: () => void;
	onExpandAll: () => void;
	onOpenDialog?: () => void;
	onToggleDisplayMode: () => void;
	onToggleWrap: () => void;
	stopPropagation?: boolean;
}

export default function JsonDisplayActions({
	asAccordion = false,
	copied,
	displayMode,
	editable,
	editing = false,
	showOpenInDialog = false,
	wrapText,
	onCollapseAll,
	onCopy,
	onEdit,
	onExpandAll,
	onOpenDialog,
	onToggleDisplayMode,
	onToggleWrap,
	stopPropagation = false,
}: JsonDisplayActionsProps) {
	const buttonBackground = asAccordion ? 'transparent' : 'background.paper';
	const stopEvent = (event: React.SyntheticEvent) => {
		if (stopPropagation) {
			event.stopPropagation();
		}
	};

	return (
		<Stack direction="row" spacing={0.5} alignItems="center" onClick={stopEvent} onFocus={stopEvent} onMouseDown={stopEvent}>
			{editable && (
				<Tooltip title="Edit">
					<span>
						<IconButton
							size="small"
							onClick={onEdit}
							disabled={editing}
							sx={{
								p: 0.5,
								bgcolor: buttonBackground,
								'&:hover': { bgcolor: 'action.hover' },
							}}
						>
							<Edit fontSize="small" />
						</IconButton>
					</span>
				</Tooltip>
			)}
			{!editing && (
				<Tooltip title={displayMode === 'json' ? 'Show tree view' : 'Show JSON view'}>
					<IconButton
						size="small"
						onClick={onToggleDisplayMode}
						color={displayMode === 'tree' ? 'primary' : 'default'}
						sx={{
							p: 0.5,
							bgcolor: buttonBackground,
							'&:hover': { bgcolor: 'action.hover' },
						}}
					>
						{displayMode === 'json' ? <AccountTree fontSize="small" /> : <Code fontSize="small" />}
					</IconButton>
				</Tooltip>
			)}
			{!editing && displayMode === 'json' && (
				<Tooltip title={wrapText ? 'Disable word wrap' : 'Enable word wrap'}>
					<IconButton
						size="small"
						onClick={onToggleWrap}
						color={wrapText ? 'primary' : 'default'}
						sx={{
							p: 0.5,
							bgcolor: buttonBackground,
							'&:hover': { bgcolor: 'action.hover' },
						}}
					>
						<WrapText fontSize="small" />
					</IconButton>
				</Tooltip>
			)}
			{!editing && displayMode === 'tree' && (
				<>
					<Tooltip title="Expand all">
						<IconButton
							size="small"
							onClick={onExpandAll}
							sx={{
								p: 0.5,
								bgcolor: buttonBackground,
								'&:hover': { bgcolor: 'action.hover' },
							}}
						>
							<UnfoldMore fontSize="small" />
						</IconButton>
					</Tooltip>
					<Tooltip title="Collapse all">
						<IconButton
							size="small"
							onClick={onCollapseAll}
							sx={{
								p: 0.5,
								bgcolor: buttonBackground,
								'&:hover': { bgcolor: 'action.hover' },
							}}
						>
							<UnfoldLess fontSize="small" />
						</IconButton>
					</Tooltip>
				</>
			)}
			{showOpenInDialog && !editing && (
				<Tooltip title="View in dialog">
					<IconButton
						size="small"
						onClick={onOpenDialog}
						sx={{
							p: 0.5,
							bgcolor: buttonBackground,
							'&:hover': { bgcolor: 'action.hover' },
						}}
					>
						<OpenInFull fontSize="small" />
					</IconButton>
				</Tooltip>
			)}
			<Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
				<IconButton
					size="small"
					onClick={onCopy}
					sx={{
						p: 0.5,
						bgcolor: buttonBackground,
						'&:hover': { bgcolor: 'action.hover' },
					}}
				>
					{copied ? <Check fontSize="small" color="success" /> : <ContentCopy fontSize="small" />}
				</IconButton>
			</Tooltip>
		</Stack>
	);
}
